import { useEffect, useState } from "react";
import DashLayout from "../components/DashLayout";
import api, { formatApiErrorDetail } from "../lib/api";
import { Users, Activity, Settings, ShieldAlert, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("stats");

    // System Settings Form
    const [storeId, setStoreId] = useState("");
    const [publicKey, setPublicKey] = useState("");
    const [webhookSecret, setWebhookSecret] = useState("");
    const [trialDurationDays, setTrialDurationDays] = useState("");
    const [updatingSettings, setUpdatingSettings] = useState(false);

    const loadData = async () => {
        try {
            const [statsRes, usersRes] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/users")
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const updateUserPlan = async (userId, newPlan) => {
        try {
            await api.patch(`/admin/users/${userId}`, { plan: newPlan });
            toast.success(`User plan updated to ${newPlan.toUpperCase()}`);
            loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update user plan");
        }
    };

    const updateUserRole = async (userId, newRole) => {
        try {
            await api.patch(`/admin/users/${userId}`, { role: newRole });
            toast.success(`User role updated to ${newRole.toUpperCase()}`);
            loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update user role");
        }
    };

    const extendTrial = async (userId, currentTrialEnd) => {
        try {
            let baseDate = new Date();
            if (currentTrialEnd) {
                const currentEnd = new Date(currentTrialEnd);
                if (currentEnd > baseDate) {
                    baseDate = currentEnd;
                }
            }
            const newEnd = new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            await api.patch(`/admin/users/${userId}`, { trial_end: newEnd.toISOString() });
            toast.success("User trial extended by 7 days");
            loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to extend trial");
        }
    };

    const expireTrial = async (userId) => {
        if (!confirm("Are you sure you want to expire this user's trial immediately?")) return;
        try {
            const newEnd = new Date();
            await api.patch(`/admin/users/${userId}`, { trial_end: newEnd.toISOString() });
            toast.success("User trial expired immediately");
            loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to expire trial");
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setUpdatingSettings(true);
        try {
            await api.put("/admin/settings", {
                freemius_store_id: storeId || null,
                freemius_public_key: publicKey || null,
                freemius_webhook_secret: webhookSecret || null,
                trial_duration_days: trialDurationDays ? Number(trialDurationDays) : null
            });
            toast.success("System configurations updated successfully");
            setStoreId("");
            setPublicKey("");
            setWebhookSecret("");
            setTrialDurationDays("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update settings");
        } finally {
            setUpdatingSettings(false);
        }
    };

    if (loading) {
        return (
            <DashLayout title="ADMIN PANEL" subtitle="loading admin details">
                <div className="text-[#52525B] text-sm font-mono">[ checking access and fetching metrics... ]</div>
            </DashLayout>
        );
    }

    return (
        <DashLayout
            title="ADMIN PANEL"
            subtitle="system workspace / admin"
            action={
                <div className="flex border border-[#27272A] bg-[#0A0A0A] font-mono text-xs">
                    <button 
                        onClick={() => setActiveTab("stats")} 
                        className={`px-4 py-2 border-r border-[#27272A] ${activeTab === "stats" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                    >
                        <Activity className="w-3.5 h-3.5 inline mr-1" /> STATS
                    </button>
                    <button 
                        onClick={() => setActiveTab("users")} 
                        className={`px-4 py-2 border-r border-[#27272A] ${activeTab === "users" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                    >
                        <Users className="w-3.5 h-3.5 inline mr-1" /> USERS
                    </button>
                    <button 
                        onClick={() => setActiveTab("settings")} 
                        className={`px-4 py-2 ${activeTab === "settings" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                    >
                        <Settings className="w-3.5 h-3.5 inline mr-1" /> BILLING & SYSTEM
                    </button>
                </div>
            }
        >
            {/* Stats Dashboard */}
            {activeTab === "stats" && stats && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A]" data-testid="admin-stats">
                        <div className="p-5 border-r border-b md:border-r border-[#27272A]">
                            <div className="label-mono">TOTAL USERS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#FAFAFA]">{stats.total_users}</div>
                        </div>
                        <div className="p-5 border-r border-b md:border-b-0 border-[#27272A]">
                            <div className="label-mono">ACTIVE TUNNELS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#00FF41]">{stats.active_tunnels}</div>
                        </div>
                        <div className="p-5">
                            <div className="label-mono">TOTAL TUNNELS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#FAFAFA]">{stats.total_tunnels}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A]" data-testid="admin-stats-trials">
                        <div className="p-5 border-r border-b md:border-b-0 border-[#27272A]">
                            <div className="label-mono">ACTIVE TRIALS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#00FF41]">{stats.active_trials}</div>
                        </div>
                        <div className="p-5 border-r border-b md:border-b-0 border-[#27272A]">
                            <div className="label-mono">EXPIRED TRIALS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#FF3B30]">{stats.expired_trials}</div>
                        </div>
                        <div className="p-5">
                            <div className="label-mono">TOTAL PROCESS REQUESTS</div>
                            <div className="font-display text-4xl font-bold mt-1 text-[#FAFAFA]">{stats.total_requests}</div>
                        </div>
                    </div>

                    <div className="tf-card-accent p-6 flex items-start gap-4">
                        <ShieldAlert className="w-6 h-6 text-[#FF9900] shrink-0 mt-0.5" />
                        <div>
                            <h4 className="font-display text-lg font-bold text-[#FAFAFA] mb-1">ADMINISTRATIVE BOUNDS</h4>
                            <p className="text-sm text-[#A1A1AA]">
                                Modifications done here directly alter user permissions, active tunnel streams, and SaaS limits. Changes to plans bypass Freemius checkout gates immediately.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* User Manager */}
            {activeTab === "users" && (
                <div className="border border-[#27272A]" data-testid="admin-users-list">
                    <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_0.8fr_2fr_1.5fr] gap-6 px-5 py-3 border-b border-[#27272A] bg-[#0A0A0A] label-mono">
                        <div>User Email</div>
                        <div>Role</div>
                        <div>Plan</div>
                        <div>Tunnels</div>
                        <div>Trial Status</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {users.map((u) => (
                        <div key={u.id} className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_0.8fr_2fr_1.5fr] gap-3 md:gap-6 px-5 py-4 border-b last:border-b-0 border-[#27272A] items-center hover:bg-[#0A0A0A]">
                            <div>
                                <span className="font-mono text-[#FAFAFA] block truncate">{u.email}</span>
                                <span className="text-[10px] text-[#52525B] font-mono">ID: {u.id}</span>
                            </div>
                            <div>
                                <select 
                                    value={u.role} 
                                    onChange={(e) => updateUserRole(u.id, e.target.value)}
                                    className="bg-[#050505] text-[#FAFAFA] border border-[#27272A] font-mono text-xs py-1 px-2 focus:border-[#00FF41] outline-none"
                                >
                                    <option value="user">USER</option>
                                    <option value="admin">ADMIN</option>
                                </select>
                            </div>
                            <div>
                                <select 
                                    value={u.plan} 
                                    onChange={(e) => updateUserPlan(u.id, e.target.value)}
                                    className="bg-[#050505] text-[#FAFAFA] border border-[#27272A] font-mono text-xs py-1 px-2 focus:border-[#00FF41] outline-none"
                                >
                                    <option value="free">FREE</option>
                                    <option value="pro">PRO</option>
                                    <option value="enterprise">ENTERPRISE</option>
                                </select>
                            </div>
                            <div className="font-mono text-sm text-[#FAFAFA]">{u.tunnels_count}</div>
                            
                            {/* Trial Details Column */}
                            <div className="font-mono text-xs space-y-1">
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-[#52525B]" />
                                    <span className="text-[#52525B]">Start:</span>{" "}
                                    <span className="text-[#FAFAFA]">{u.trial_start ? new Date(u.trial_start).toLocaleDateString() : "—"}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-[#52525B]" />
                                    <span className="text-[#52525B]">End:</span>{" "}
                                    <span className="text-[#FAFAFA]">{u.trial_end ? new Date(u.trial_end).toLocaleDateString() : "—"}</span>
                                </div>
                                <div>
                                    <span className="text-[#52525B]">Status:</span>{" "}
                                    {u.trial_expired ? (
                                        <span className="text-[#FF3B30] font-bold">EXPIRED</span>
                                    ) : (
                                        <span className="text-[#00FF41] font-bold">ACTIVE</span>
                                    )}
                                </div>
                            </div>

                            {/* Actions Column */}
                            <div className="flex flex-col sm:flex-row justify-end gap-2">
                                <button
                                    onClick={() => extendTrial(u.id, u.trial_end)}
                                    className="tf-btn tf-btn-ghost !py-1.5 !px-3 text-xs"
                                    data-testid={`extend-trial-${u.id}`}
                                >
                                    +7 Days
                                </button>
                                <button
                                    onClick={() => expireTrial(u.id)}
                                    className="tf-btn tf-btn-danger !py-1.5 !px-3 text-xs"
                                    data-testid={`expire-trial-${u.id}`}
                                >
                                    Expire
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* System settings / Freemius credentials */}
            {activeTab === "settings" && (
                <div className="tf-card-accent max-w-xl p-8 border border-[#27272A]">
                    <div className="label-mono text-[#00FF41] mb-2">// System & Billing Configuration</div>
                    <h3 className="heading-md mb-6">UPDATE SYSTEM CONFIGS</h3>
                    <form onSubmit={saveSettings} className="space-y-6">
                        <div>
                            <div className="label-mono mb-1">DEFAULT TRIAL DURATION (DAYS)</div>
                            <input 
                                type="number"
                                min="1"
                                className="tf-input pl-2 font-mono text-xs text-[#FAFAFA] w-full" 
                                placeholder="e.g. 7" 
                                value={trialDurationDays} 
                                onChange={(e) => setTrialDurationDays(e.target.value)} 
                            />
                        </div>
                        <div>
                            <div className="label-mono mb-1">FREEMIUS STORE ID</div>
                            <input 
                                className="tf-input pl-2 font-mono text-xs text-[#FAFAFA] w-full" 
                                placeholder="e.g. 12345" 
                                value={storeId} 
                                onChange={(e) => setStoreId(e.target.value)} 
                            />
                        </div>
                        <div>
                            <div className="label-mono mb-1">FREEMIUS PUBLIC KEY</div>
                            <input 
                                className="tf-input pl-2 font-mono text-xs text-[#FAFAFA] w-full" 
                                placeholder="e.g. pk_abc123..." 
                                value={publicKey} 
                                onChange={(e) => setPublicKey(e.target.value)} 
                            />
                        </div>
                        <div>
                            <div className="label-mono mb-1">FREEMIUS WEBHOOK SECRET</div>
                            <input 
                                type="password"
                                className="tf-input pl-2 font-mono text-xs text-[#FAFAFA] w-full" 
                                placeholder="e.g. fs_secret_..." 
                                value={webhookSecret} 
                                onChange={(e) => setWebhookSecret(e.target.value)} 
                            />
                        </div>
                        <button 
                            type="submit" 
                            disabled={updatingSettings} 
                            className="tf-btn w-full justify-center"
                        >
                            {updatingSettings ? "SAVING..." : "SAVE CONFIGURATIONS"}
                        </button>
                    </form>
                </div>
            )}
        </DashLayout>
    );
}
